import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	ChevronRight,
	CircleAlert,
	CircleCheck,
	CircleX,
	ExternalLink,
	LoaderCircle,
	Plus,
	Sparkles,
	X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { api, type InboxList } from "@/client";
import { MetricStrip } from "@/components/MetricStrip";
import { TransactionFiscalDocuments } from "@/components/TransactionFiscalDocuments";
import { INBOX_SOURCE_DESCRIPTIONS, INBOX_SOURCE_LABELS } from "@/domain/inbox";
import { EXPENSE_CATEGORIES, OPERATING_EXPENSE_DESTINATION } from "@/domain/operating-expenses";
import { FinancePresentation } from "@/presentation/finance";

type Candidate = InboxList["candidates"][number];
type OriginFilter = "ALL" | Candidate["source"];
type PeriodFilter = "ALL" | "OVERDUE" | "CURRENT" | "NEXT";
type StateFilter = "ALL" | Candidate["state"];

interface ManualCandidateForm {
	supplierId: string;
	creditorName: string;
	amount: number;
	dueOn: string;
	description: string;
}

const signalLabels: Record<Candidate["signals"][number], string> = {
	SUPPLIER_UNKNOWN: "Fornecedor não reconhecido",
	FISCAL_DOCUMENT_ABSENT: "Sem documento fiscal",
	FISCAL_DOCUMENT_TO_REVIEW: "Documento para conferir",
	POSSIBLE_DUPLICATE: "Possível duplicidade",
};

const qiveStatusLabels: Record<string, string> = {
	open: "Aberto na Qive",
	overdue: "Vencido na Qive",
	paid: "Pago na Qive",
	canceled: "Cancelado na Qive",
	closed: "Encerrado na Qive",
	blocked: "Bloqueado na Qive",
};

const qiveTrustLabels: Record<string, string> = {
	high: "alta",
	medium: "média",
	low: "baixa",
};

const candidateStateLabels: Record<Candidate["state"], string> = {
	PENDING: "Aguardando decisão",
	RECOGNIZED: "Reconhecido",
	DISMISSED: "Descartado",
	CANCELLED: "Cancelado pela OC",
};

export function InboxView() {
	const queryClient = useQueryClient();
	const [origin, setOrigin] = useState<OriginFilter>("ALL");
	const [period, setPeriod] = useState<PeriodFilter>("ALL");
	const [state, setState] = useState<StateFilter>("PENDING");
	const [search, setSearch] = useState("");
	const [selectedId, setSelectedId] = useState<string>();
	const [showManualForm, setShowManualForm] = useState(false);
	const query = useQuery({
		queryKey: ["inbox"],
		queryFn: () => api.inbox.list(),
	});
	const form = useForm<ManualCandidateForm>({
		defaultValues: {
			supplierId: "",
			creditorName: "",
			amount: 0,
			dueOn: "",
			description: "",
		},
	});
	const createMutation = useMutation({
		mutationFn: (input: ManualCandidateForm) =>
			api.inbox.create({
				...(input.supplierId
					? { supplierId: input.supplierId }
					: { creditorName: input.creditorName.trim() }),
				amount: input.amount,
				dueOn: input.dueOn,
				description: input.description.trim(),
			}),
		onSuccess: async () => {
			form.reset();
			setShowManualForm(false);
			await queryClient.invalidateQueries({ queryKey: ["inbox"] });
		},
	});
	const recognizeMutation = useMutation({
		mutationFn: (input: {
			candidateId: string;
			destination: string;
			expenseCategoryId: (typeof EXPENSE_CATEGORIES)[number]["id"] | null;
		}) => api.inbox.recognize(input),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["inbox"] }),
				queryClient.invalidateQueries({ queryKey: ["payables"] }),
			]);
		},
	});
	const dismissMutation = useMutation({
		mutationFn: (input: { candidateId: string; reason: string }) => api.inbox.dismiss(input),
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["inbox"] }),
				queryClient.invalidateQueries({ queryKey: ["payables"] }),
			]);
		},
	});
	const candidates = useMemo(() => {
		if (!query.data) {
			return [];
		}

		const normalizedSearch = search.trim().toLocaleLowerCase("pt-BR");

		return query.data.candidates.filter((candidate) => {
			const matchesOrigin = origin === "ALL" || candidate.source === origin;
			const matchesState = state === "ALL" || candidate.state === state;
			const matchesPeriod =
				period === "ALL" ||
				(period === "OVERDUE" ? candidate.overdue : candidate.period === period);
			const matchesSearch =
				!normalizedSearch ||
				`${candidate.creditorName} ${candidate.supplier?.name ?? ""} ${candidate.description}`
					.toLocaleLowerCase("pt-BR")
					.includes(normalizedSearch);

			return matchesOrigin && matchesState && matchesPeriod && matchesSearch;
		});
	}, [origin, period, query.data, search, state]);

	useEffect(() => {
		if (!selectedId || !candidates.some((candidate) => candidate.id === selectedId)) {
			setSelectedId(candidates.at(0)?.id);
		}
	}, [candidates, selectedId]);

	if (query.isPending) {
		return (
			<div className="grid min-h-80 place-items-center text-sm font-medium text-stone-500">
				<span className="inline-flex items-center gap-2">
					<LoaderCircle className="size-4 animate-spin" />
					Abrindo a Caixa de entrada…
				</span>
			</div>
		);
	}

	if (query.error) {
		return (
			<div className="rounded-3xl border border-red-200 bg-white p-8 text-center">
				<CircleAlert className="mx-auto size-8 text-red-600" />
				<p className="mt-3 font-semibold text-stone-900">Não foi possível abrir a Caixa</p>
				<p className="mt-1 text-sm text-stone-500">{query.error.message}</p>
			</div>
		);
	}

	const data = query.data;
	const selected = candidates.find((candidate) => candidate.id === selectedId);

	return (
		<>
			<div className="mb-4">
				<MetricStrip
					items={[
						{
							label: "Aguardando decisão",
							value: String(data.summary.pendingCount),
							detail: `${data.summary.capturedCount} capturados`,
							tone: data.summary.pendingCount > 0 ? "attention" : "success",
						},
						{
							label: "Volume para tratar",
							value: FinancePresentation.money(data.summary.pendingAmountCents),
							detail: `${data.summary.possibleDuplicateCount} possíveis duplicidades`,
						},
						{
							label: "Pagamentos encontrados",
							value: String(data.summary.paymentFoundCount),
							detail: FinancePresentation.money(data.summary.paymentFoundAmountCents),
							tone: "success",
						},
						{
							label: "Reconhecidos no A Pagar",
							value: String(data.summary.recognizedCount),
							detail: `${data.summary.dismissedCount} descartados · ${data.summary.cancelledCount} cancelados`,
							tone: "success",
						},
					]}
				/>
			</div>

			{showManualForm && (
				<ManualCandidatePanel
					data={data}
					form={form}
					pending={createMutation.isPending}
					error={createMutation.error}
					onClose={() => setShowManualForm(false)}
					onSubmit={form.handleSubmit((input) => createMutation.mutate(input))}
				/>
			)}

			<section className="overflow-hidden rounded-[1.75rem] border border-stone-200 bg-white shadow-[0_20px_60px_-35px_rgba(28,25,23,0.35)]">
				<div className="grid min-h-[680px] xl:grid-cols-[minmax(480px,0.95fr)_minmax(420px,1.05fr)]">
					<div className="border-b border-stone-200 xl:border-r xl:border-b-0">
						<header className="space-y-3 border-b border-stone-200 p-4">
							<div className="flex items-start justify-between gap-3">
								<div>
									<div className="flex items-center gap-2">
										<h2 className="font-semibold text-stone-950">Candidatos a obrigação</h2>
										<span className="text-xs text-stone-500">{candidates.length} na lista</span>
									</div>
									<p className="mt-1 text-[11px] leading-4 text-stone-500">
										{syncDescription(data.sync)}
									</p>
								</div>
								<button
									type="button"
									onClick={() => setShowManualForm(true)}
									className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-xl bg-emerald-950 px-3 text-xs font-semibold text-white transition hover:bg-emerald-900"
								>
									<Plus className="size-3.5" />
									Novo manual
								</button>
							</div>
							<div className="grid gap-2 sm:grid-cols-2">
								<input
									type="search"
									value={search}
									onChange={(event) => setSearch(event.target.value)}
									placeholder="Buscar credor ou descrição"
									className="h-9 rounded-xl border border-stone-200 px-3 text-xs outline-none transition focus:border-emerald-700 sm:col-span-2"
								/>
								<select
									value={origin}
									onChange={(event) => {
										const value = event.target.value;
										setOrigin(
											value === "QIVE" || value === "MANUAL" || value === "LOGISTICA"
												? value
												: "ALL",
										);
									}}
									className="h-9 rounded-xl border border-stone-200 bg-white px-3 text-xs outline-none focus:border-emerald-700"
								>
									<option value="ALL">Todas as origens</option>
									<option value="QIVE">Qive</option>
									<option value="MANUAL">Manual</option>
									<option value="LOGISTICA">Módulo Logístico</option>
								</select>
								<select
									value={state}
									onChange={(event) => {
										const value = event.target.value;
										setState(
											value === "PENDING" ||
												value === "RECOGNIZED" ||
												value === "DISMISSED" ||
												value === "CANCELLED"
												? value
												: "ALL",
										);
									}}
									className="h-9 rounded-xl border border-stone-200 bg-white px-3 text-xs outline-none focus:border-emerald-700"
								>
									<option value="PENDING">Aguardando decisão</option>
									<option value="RECOGNIZED">Reconhecidos</option>
									<option value="DISMISSED">Descartados</option>
									<option value="CANCELLED">Cancelados pela OC</option>
									<option value="ALL">Todos os estados</option>
								</select>
								<select
									value={period}
									onChange={(event) => {
										const value = event.target.value;
										setPeriod(
											value === "OVERDUE" || value === "CURRENT" || value === "NEXT"
												? value
												: "ALL",
										);
									}}
									className="h-9 rounded-xl border border-stone-200 bg-white px-3 text-xs outline-none focus:border-emerald-700 sm:col-span-2"
								>
									<option value="ALL">Todo o recorte</option>
									<option value="OVERDUE">Vencidos</option>
									<option value="CURRENT">Mês atual</option>
									<option value="NEXT">Próximo mês</option>
								</select>
							</div>
						</header>

						<div className="max-h-[528px] overflow-y-auto">
							{candidates.length === 0 ? (
								<p className="px-5 py-12 text-center text-sm text-stone-500">
									Nenhum candidato corresponde aos filtros.
								</p>
							) : (
								candidates.map((candidate) => (
									<CandidateRow
										key={candidate.id}
										candidate={candidate}
										selected={candidate.id === selectedId}
										showState={state !== "PENDING"}
										onSelect={() => setSelectedId(candidate.id)}
									/>
								))
							)}
						</div>
					</div>

					<CandidatePanel
						candidate={selected}
						candidates={data.candidates}
						onSelect={setSelectedId}
						onRecognize={(input) => recognizeMutation.mutate(input)}
						onDismiss={(reason) => {
							if (selected) {
								dismissMutation.mutate({ candidateId: selected.id, reason });
							}
						}}
						recognitionPending={
							recognizeMutation.isPending &&
							recognizeMutation.variables?.candidateId === selected?.id
						}
						dismissalPending={
							dismissMutation.isPending && dismissMutation.variables?.candidateId === selected?.id
						}
						actionError={
							recognizeMutation.variables?.candidateId === selected?.id
								? recognizeMutation.error
								: dismissMutation.variables?.candidateId === selected?.id
									? dismissMutation.error
									: null
						}
					/>
				</div>
			</section>
		</>
	);
}

function ManualCandidatePanel({
	data,
	form,
	pending,
	error,
	onClose,
	onSubmit,
}: {
	data: InboxList;
	form: ReturnType<typeof useForm<ManualCandidateForm>>;
	pending: boolean;
	error: Error | null;
	onClose: () => void;
	onSubmit: () => void;
}) {
	const supplierId = form.watch("supplierId");

	return (
		<section className="mb-6 rounded-3xl border border-stone-200 bg-white p-5 shadow-sm">
			<div className="flex items-start justify-between gap-4">
				<div>
					<h2 className="font-semibold text-stone-950">Registrar candidato manual</h2>
					<p className="mt-1 text-xs leading-5 text-stone-500">
						Use um fornecedor cadastrado quando conhecido. Texto livre não cria fornecedor
						automaticamente.
					</p>
				</div>
				<button
					type="button"
					onClick={onClose}
					aria-label="Fechar"
					className="grid size-9 place-items-center rounded-xl text-stone-500 transition hover:bg-stone-100"
				>
					<X className="size-4" />
				</button>
			</div>

			<form
				onSubmit={(event) => {
					event.preventDefault();
					onSubmit();
				}}
				className="mt-5 grid gap-4 lg:grid-cols-2"
			>
				<label className="grid gap-1.5 text-xs font-medium text-stone-600">
					Fornecedor cadastrado
					<select
						{...form.register("supplierId")}
						className="h-11 rounded-xl border border-stone-200 bg-white px-3 text-sm text-stone-900 outline-none focus:border-emerald-700"
					>
						<option value="">Ainda não cadastrado</option>
						{data.suppliers.map((supplier) => (
							<option key={supplier.id} value={supplier.id}>
								{supplier.name}
							</option>
						))}
					</select>
				</label>
				<label className="grid gap-1.5 text-xs font-medium text-stone-600">
					Credor {!supplierId && <span className="text-red-600">obrigatório</span>}
					<input
						{...form.register("creditorName", { required: !supplierId })}
						disabled={!!supplierId}
						placeholder="Nome de quem receberá"
						className="h-11 rounded-xl border border-stone-200 px-3 text-sm text-stone-900 outline-none focus:border-emerald-700 disabled:bg-stone-50 disabled:text-stone-400"
					/>
				</label>
				<label className="grid gap-1.5 text-xs font-medium text-stone-600">
					Valor
					<input
						type="number"
						min="0.01"
						step="0.01"
						{...form.register("amount", { required: true, min: 0.01, valueAsNumber: true })}
						className="h-11 rounded-xl border border-stone-200 px-3 text-sm text-stone-900 outline-none focus:border-emerald-700"
					/>
				</label>
				<label className="grid gap-1.5 text-xs font-medium text-stone-600">
					Vencimento
					<input
						type="date"
						min={data.horizon.from}
						max={data.horizon.to}
						{...form.register("dueOn", { required: true })}
						className="h-11 rounded-xl border border-stone-200 px-3 text-sm text-stone-900 outline-none focus:border-emerald-700"
					/>
				</label>
				<label className="grid gap-1.5 text-xs font-medium text-stone-600 lg:col-span-2">
					Descrição
					<textarea
						rows={3}
						{...form.register("description", { required: true })}
						placeholder="O que a equipe já sabe sobre este compromisso?"
						className="rounded-xl border border-stone-200 px-3 py-2.5 text-sm text-stone-900 outline-none focus:border-emerald-700"
					/>
				</label>
				{error && (
					<p className="text-sm text-red-700 lg:col-span-2">
						{error.message || "Não foi possível registrar o candidato."}
					</p>
				)}
				<div className="flex justify-end gap-2 lg:col-span-2">
					<button
						type="button"
						onClick={onClose}
						className="h-10 rounded-xl border border-stone-200 px-4 text-sm font-semibold text-stone-600 hover:bg-stone-50"
					>
						Cancelar
					</button>
					<button
						type="submit"
						disabled={pending}
						className="inline-flex h-10 items-center gap-2 rounded-xl bg-emerald-950 px-4 text-sm font-semibold text-white disabled:cursor-wait disabled:opacity-60"
					>
						{pending && <LoaderCircle className="size-4 animate-spin" />}
						Registrar candidato
					</button>
				</div>
			</form>
		</section>
	);
}

function CandidateRow({
	candidate,
	selected,
	showState,
	onSelect,
}: {
	candidate: Candidate;
	selected: boolean;
	showState: boolean;
	onSelect: () => void;
}) {
	const suggestedDocuments = candidate.documents.filter(
		(document) => document.associationState === "SUGGESTED",
	);
	const possibleDuplicate = candidate.signals.includes("POSSIBLE_DUPLICATE");

	return (
		<button
			type="button"
			onClick={onSelect}
			className={`grid w-full grid-cols-[72px_minmax(0,1fr)_auto_20px] items-center gap-3 border-b border-stone-100 px-4 py-3 text-left transition last:border-b-0 ${
				selected ? "bg-emerald-50/70" : "hover:bg-stone-50"
			}`}
		>
			<div>
				<p
					className={`text-[10px] font-semibold uppercase tracking-wide ${
						candidate.postedPayment
							? "text-emerald-700"
							: candidate.overdue
								? "text-amber-700"
								: "text-stone-400"
					}`}
				>
					{candidate.postedPayment ? "Encontrado" : candidate.overdue ? "Venceu" : "Vence"}
				</p>
				<p
					className={`mt-0.5 text-xs font-semibold ${
						candidate.postedPayment
							? "text-emerald-800"
							: candidate.overdue
								? "text-amber-800"
								: "text-stone-700"
					}`}
				>
					{FinancePresentation.transactionDate(
						candidate.postedPayment ? candidate.postedPayment.occurredOn : candidate.dueOn,
					)}
				</p>
			</div>
			<div className="min-w-0">
				<p className="truncate text-sm font-semibold text-stone-900">
					{candidate.supplier?.name ?? candidate.creditorName}
				</p>
				<p className="mt-0.5 truncate text-[11px] text-stone-500">
					{INBOX_SOURCE_LABELS[candidate.source]} · {candidate.description}
				</p>
				{(candidate.postedPayment ||
					possibleDuplicate ||
					suggestedDocuments.length > 0 ||
					(showState && candidate.state !== "PENDING")) && (
					<div className="mt-1.5 flex flex-wrap gap-1">
						{candidate.postedPayment && <Badge tone="green">Pagamento encontrado</Badge>}
						{possibleDuplicate && <Badge tone="amber">Possível duplicidade</Badge>}
						{suggestedDocuments.length > 0 && (
							<Badge tone="amber">
								{suggestedDocuments.length} documento
								{suggestedDocuments.length === 1 ? "" : "s"} para conferir
							</Badge>
						)}
						{showState && candidate.state === "RECOGNIZED" && (
							<Badge tone="green">Reconhecido</Badge>
						)}
						{showState && candidate.state === "DISMISSED" && <Badge tone="stone">Descartado</Badge>}
						{showState && candidate.state === "CANCELLED" && (
							<Badge tone="stone">Cancelado pela OC</Badge>
						)}
					</div>
				)}
			</div>
			<p className="text-right text-sm font-semibold tabular-nums text-stone-950">
				{FinancePresentation.money(candidate.amountCents)}
			</p>
			<ChevronRight className={`size-4 ${selected ? "text-emerald-800" : "text-stone-300"}`} />
		</button>
	);
}

function CandidatePanel({
	candidate,
	candidates,
	onSelect,
	onRecognize,
	onDismiss,
	recognitionPending,
	dismissalPending,
	actionError,
}: {
	candidate?: Candidate;
	candidates: Candidate[];
	onSelect: (candidateId: string) => void;
	onRecognize: (input: {
		candidateId: string;
		destination: string;
		expenseCategoryId: (typeof EXPENSE_CATEGORIES)[number]["id"] | null;
	}) => void;
	onDismiss: (reason: string) => void;
	recognitionPending: boolean;
	dismissalPending: boolean;
	actionError: Error | null;
}) {
	if (!candidate) {
		return (
			<aside className="grid min-h-80 place-items-center bg-[#fbfbf7] p-8 text-center text-sm text-stone-500">
				Selecione um candidato para compreender seus fatos e decidir.
			</aside>
		);
	}

	const linkedDocuments = candidate.documents.filter(
		(document) => document.associationState === "LINKED",
	);
	const suggestedDocuments = candidate.documents.filter(
		(document) => document.associationState === "SUGGESTED",
	);
	const similar = similarCandidates(candidate, candidates);

	return (
		<aside className="max-h-[680px] overflow-y-auto bg-[#fbfbf7]">
			<header className="border-b border-stone-200 bg-white p-5">
				<div className="flex items-start justify-between gap-4">
					<div className="min-w-0">
						<p className="text-xs font-semibold uppercase tracking-[0.14em] text-stone-500">
							Compreender candidato
						</p>
						<h2 className="mt-2 truncate text-xl font-semibold tracking-[-0.025em] text-stone-950">
							{candidate.supplier?.name ?? candidate.creditorName}
						</h2>
						<p
							className={`mt-1 text-xs ${
								candidate.postedPayment
									? "font-semibold text-emerald-700"
									: candidate.overdue
										? "font-semibold text-amber-700"
										: "text-stone-500"
							}`}
						>
							{candidate.postedPayment ? (
								<>
									Pagamento encontrado no banco em{" "}
									{FinancePresentation.transactionDate(candidate.postedPayment.occurredOn)} ·{" "}
									{INBOX_SOURCE_LABELS[candidate.source]}
								</>
							) : (
								<>
									{candidate.overdue ? "Venceu" : "Vence"}{" "}
									{FinancePresentation.transactionDate(candidate.dueOn)} ·{" "}
									{INBOX_SOURCE_LABELS[candidate.source]}
								</>
							)}
						</p>
					</div>
					<p className="shrink-0 text-lg font-semibold tabular-nums text-stone-950">
						{FinancePresentation.money(candidate.amountCents)}
					</p>
				</div>
				<div className="mt-3 flex flex-wrap gap-1">
					{candidate.supplier && <Badge tone="green">Fornecedor reconhecido</Badge>}
					{candidate.postedPayment && <Badge tone="green">Pagamento encontrado</Badge>}
					{candidate.classificationSuggestion && candidate.state === "PENDING" && (
						<Badge tone="blue">Classificação sugerida</Badge>
					)}
					{linkedDocuments.length > 0 && (
						<Badge tone="green">
							{linkedDocuments.length} documento{linkedDocuments.length === 1 ? "" : "s"} ligado
						</Badge>
					)}
					{candidate.signals.map((signal) => (
						<Badge key={signal} tone={signal === "POSSIBLE_DUPLICATE" ? "amber" : "stone"}>
							{signalLabels[signal]}
						</Badge>
					))}
				</div>
			</header>

			<div className="space-y-5 p-5">
				{candidate.postedPayment && (
					<section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
						<div className="flex items-start gap-3">
							<CircleCheck className="mt-0.5 size-5 shrink-0 text-emerald-700" />
							<div>
								<h3 className="text-sm font-semibold text-emerald-950">
									Pagamento encontrado no banco
								</h3>
								<p className="mt-1 text-xs leading-5 text-emerald-900/80">
									A conta principal possui uma saída efetivada e única que corresponde a este
									candidato por fornecedor, valor e vencimento. Ainda não existe uma obrigação:
									reconhecer confirma o compromisso e confronta novamente esta saída para
									conciliá-la.
								</p>
							</div>
						</div>
						<dl className="mt-4 grid gap-3 text-xs sm:grid-cols-2">
							<Fact
								label="Data bancária"
								value={FinancePresentation.transactionDate(candidate.postedPayment.occurredOn)}
							/>
							<Fact
								label="Valor encontrado"
								value={FinancePresentation.money(candidate.postedPayment.amountCents)}
							/>
							<Fact
								label="Recebedor"
								value={candidate.postedPayment.receiverName ?? "Não informado pelo banco"}
							/>
							<Fact
								label="Meio"
								value={candidate.postedPayment.paymentMethod ?? "Não informado pelo banco"}
							/>
						</dl>
						<p className="mt-3 text-[11px] leading-5 text-emerald-900/70">
							O estado informado pela Qive não participa desta verificação bancária.
						</p>
					</section>
				)}

				<section>
					<h3 className="text-sm font-semibold text-stone-900">Fatos capturados</h3>
					<dl className="mt-3 grid gap-3 text-xs sm:grid-cols-2">
						<Fact label="Credor" value={candidate.creditorName} />
						<Fact
							label="Identidade fiscal"
							value={
								candidate.creditorTaxId
									? FinancePresentation.taxId(candidate.creditorTaxId)
									: "Não informada"
							}
						/>
						<Fact label="Origem" value={INBOX_SOURCE_DESCRIPTIONS[candidate.source]} />
						<Fact
							label="Estado na origem"
							value={
								candidate.source === "LOGISTICA"
									? "Compra contratada"
									: candidate.sourceStatus
										? (qiveStatusLabels[candidate.sourceStatus] ?? candidate.sourceStatus)
										: "Não se aplica ao registro manual"
							}
						/>
						{candidate.issuedOn && (
							<Fact
								label="Emissão da cobrança"
								value={FinancePresentation.transactionDate(candidate.issuedOn)}
							/>
						)}
						<Fact label="Capturado em" value={FinancePresentation.moment(candidate.firstSeenAt)} />
						<Fact
							label="Última observação"
							value={FinancePresentation.moment(candidate.lastSeenAt)}
						/>
					</dl>
					<div className="mt-4 rounded-2xl border border-stone-200 bg-white p-4">
						<p className="text-[10px] font-medium uppercase tracking-wide text-stone-400">
							Descrição
						</p>
						<p className="mt-1 text-sm leading-6 text-stone-700">{candidate.description}</p>
						{candidate.createdBy && (
							<p className="mt-2 text-xs text-stone-500">Registrado por {candidate.createdBy}</p>
						)}
					</div>
					{candidate.source === "QIVE" && (
						<a
							href={`/api/inbox-billet.pdf?id=${encodeURIComponent(candidate.id)}`}
							target="_blank"
							rel="noreferrer"
							className="mt-3 inline-flex items-center gap-2 rounded-xl border border-stone-200 bg-white px-3 py-2 text-xs font-semibold text-emerald-900 transition hover:bg-emerald-50"
						>
							Ver boleto PDF
							<ExternalLink className="size-3.5" />
						</a>
					)}
				</section>

				<section>
					<h3 className="text-sm font-semibold text-stone-900">Contexto documental</h3>
					<div className="mt-3">
						<TransactionFiscalDocuments
							documents={linkedDocuments}
							showEmpty={suggestedDocuments.length === 0}
							emptyExplanation="Nenhum documento fiscal foi ligado a esta cobrança. Isso exige revisão, mas não prova que o candidato seja inválido."
							linkExplanation="Vínculo documental direto da Qive, correspondência integral exata ou fechamento único do grupo de parcelas. Boleto e documento fiscal permanecem fatos distintos."
						/>
					</div>
					{suggestedDocuments.length > 0 && (
						<div className="mt-4">
							<p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
								Documento para conferir
							</p>
							<TransactionFiscalDocuments
								documents={suggestedDocuments}
								linkExplanation="Sugestão única por CNPJ e valor numa janela de sete dias. Reconhecer a obrigação não confirma automaticamente este vínculo documental."
							/>
						</div>
					)}
					{candidate.source === "QIVE" && candidate.sourceTrustLevel && (
						<p className="mt-3 text-[11px] leading-5 text-stone-500">
							A Qive informou confiança{" "}
							{qiveTrustLabels[candidate.sourceTrustLevel] ?? candidate.sourceTrustLevel} para a
							associação. Isso é um fato da fonte, não uma conciliação do SINDI Financeiro.
						</p>
					)}
					{candidate.source === "LOGISTICA" && (
						<p className="mt-3 text-[11px] leading-5 text-stone-500">
							Compromisso reconhecido a partir da OC efetivada no Módulo Logístico. O pagamento e a
							conciliação permanecem sob responsabilidade do SINDI Financeiro.
						</p>
					)}
				</section>

				{candidate.signals.includes("POSSIBLE_DUPLICATE") && (
					<section className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
						<h3 className="text-sm font-semibold text-amber-950">Semelhantes na Caixa</h3>
						<p className="mt-1 text-[11px] leading-4 text-amber-900/70">
							Busca visual por mesmo valor e identidade. Não cria vínculo nem prova duplicidade.
						</p>
						<div className="mt-3 space-y-2">
							{similar.map((item) => (
								<button
									key={item.id}
									type="button"
									onClick={() => onSelect(item.id)}
									className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-3 rounded-xl border border-amber-200 bg-white p-3 text-left transition hover:border-amber-400"
								>
									<span className="min-w-0">
										<span className="block truncate text-xs font-semibold text-stone-900">
											{item.supplier?.name ?? item.creditorName}
										</span>
										<span className="mt-1 block text-[11px] text-stone-500">
											{FinancePresentation.transactionDate(item.dueOn)} ·{" "}
											{candidateStateLabels[item.state]} ·{" "}
											{FinancePresentation.moment(item.firstSeenAt)}
										</span>
									</span>
									<span className="text-xs font-semibold tabular-nums text-stone-900">
										{FinancePresentation.money(item.amountCents)}
									</span>
								</button>
							))}
							{similar.length === 0 && (
								<p className="text-xs text-amber-900">
									Nenhum outro candidato semelhante aparece na Caixa de entrada.
								</p>
							)}
						</div>
					</section>
				)}

				{candidate.state === "PENDING" ? (
					<InboxDecisionPanel
						key={candidate.id}
						candidate={candidate}
						onRecognize={onRecognize}
						onDismiss={onDismiss}
						recognitionPending={recognitionPending}
						dismissalPending={dismissalPending}
						error={actionError}
					/>
				) : (
					<div className="rounded-2xl border border-stone-200 bg-white p-4">
						<div className="flex items-start gap-3">
							{candidate.state === "RECOGNIZED" ? (
								<CircleCheck className="mt-0.5 size-5 text-emerald-700" />
							) : (
								<CircleX className="mt-0.5 size-5 text-stone-500" />
							)}
							<div>
								<p className="text-sm font-semibold text-stone-900">
									{candidate.state === "RECOGNIZED"
										? "Compromisso reconhecido no A Pagar"
										: candidate.state === "CANCELLED"
											? "Compromisso cancelado pela ordem de compra"
											: "Candidato descartado"}
								</p>
								<p className="mt-1 text-xs leading-5 text-stone-500">
									{candidate.state === "CANCELLED" ? (
										<>
											{candidate.decision?.cancellationReason ?? "Cancelamento registrado."} Por{" "}
											{candidate.decision?.cancelledBy ?? "usuário não identificado"} em{" "}
											{candidate.decision?.cancelledAt
												? FinancePresentation.moment(candidate.decision.cancelledAt)
												: "momento não informado"}
											.
										</>
									) : (
										<>
											{candidate.decision?.reason ? `${candidate.decision.reason} — ` : ""}
											Decisão registrada por{" "}
											{candidate.decision?.actor ?? "usuário não identificado"} em{" "}
											{candidate.decision
												? FinancePresentation.moment(candidate.decision.createdAt)
												: "momento não informado"}
											.
										</>
									)}
								</p>
							</div>
						</div>
					</div>
				)}
			</div>
		</aside>
	);
}

function similarCandidates(candidate: Candidate, candidates: Candidate[]) {
	if (!candidate.signals.includes("POSSIBLE_DUPLICATE")) {
		return [];
	}

	const creditor = normalizedIdentity(candidate.creditorName);

	return candidates.filter(
		(item) =>
			item.id !== candidate.id &&
			item.amountCents === candidate.amountCents &&
			((!!candidate.creditorTaxId &&
				!!item.creditorTaxId &&
				candidate.creditorTaxId === item.creditorTaxId) ||
				normalizedIdentity(item.creditorName) === creditor),
	);
}

function normalizedIdentity(value: string) {
	return value
		.normalize("NFD")
		.replace(/\p{Mark}/gu, "")
		.toLocaleLowerCase("pt-BR")
		.replace(/\s+/gu, " ")
		.trim();
}

function InboxDecisionPanel({
	candidate,
	onRecognize,
	onDismiss,
	recognitionPending,
	dismissalPending,
	error,
}: {
	candidate: Candidate;
	onRecognize: (input: {
		candidateId: string;
		destination: string;
		expenseCategoryId: (typeof EXPENSE_CATEGORIES)[number]["id"] | null;
	}) => void;
	onDismiss: (reason: string) => void;
	recognitionPending: boolean;
	dismissalPending: boolean;
	error: Error | null;
}) {
	const suggestion = candidate.classificationSuggestion;
	const [destination, setDestination] = useState(suggestion?.destination ?? "");
	const [expenseCategoryId, setExpenseCategoryId] = useState<
		(typeof EXPENSE_CATEGORIES)[number]["id"] | ""
	>(suggestion?.expenseCategory?.id ?? "");
	const [dismissReason, setDismissReason] = useState("");
	const operatingExpense = destination === OPERATING_EXPENSE_DESTINATION;
	const validClassification = !!destination.trim() && (!operatingExpense || !!expenseCategoryId);

	return (
		<section className="mt-5 rounded-2xl border border-emerald-900/10 bg-white p-5">
			<div className="flex items-start gap-3">
				<span className="grid size-9 shrink-0 place-items-center rounded-xl bg-sky-50 text-sky-800">
					<Sparkles className="size-4" />
				</span>
				<div>
					<h3 className="text-sm font-semibold text-stone-900">Tratar candidato</h3>
					<p className="mt-1 text-xs leading-5 text-stone-500">
						{candidate.postedPayment
							? "A sugestão ajuda a decidir. Reconhecer cria a obrigação e confere novamente a saída encontrada para conciliá-la."
							: "A sugestão ajuda a decidir. Somente o reconhecimento cria uma obrigação no A Pagar."}
					</p>
				</div>
			</div>

			<div className="mt-4 grid gap-4 lg:grid-cols-2">
				<label className="grid gap-1.5 text-xs font-medium text-stone-600">
					Classificação da obrigação
					<input
						value={destination}
						onChange={(event) => {
							const value = event.target.value;
							setDestination(value);

							if (value !== OPERATING_EXPENSE_DESTINATION) {
								setExpenseCategoryId("");
							}
						}}
						list={`destinations-${candidate.id}`}
						placeholder="Informe a natureza do compromisso"
						className="h-11 rounded-xl border border-stone-200 px-3 text-sm text-stone-900 outline-none focus:border-emerald-700"
					/>
					<datalist id={`destinations-${candidate.id}`}>
						<option value="Açúcar" />
						<option value="Embalagens" />
						<option value="Frete de entrada" />
						<option value="Frete de saída — CIF" />
						<option value={OPERATING_EXPENSE_DESTINATION} />
						<option value="GNRE" />
					</datalist>
				</label>
				<label className="grid gap-1.5 text-xs font-medium text-stone-600">
					Categoria
					<select
						value={expenseCategoryId}
						disabled={!operatingExpense}
						onChange={(event) => {
							const value = event.target.value;
							const category = EXPENSE_CATEGORIES.find(
								(expenseCategory) => expenseCategory.id === value,
							);

							setExpenseCategoryId(category?.id ?? "");
						}}
						className="h-11 rounded-xl border border-stone-200 bg-white px-3 text-sm text-stone-900 outline-none focus:border-emerald-700 disabled:bg-stone-50 disabled:text-stone-400"
					>
						<option value="">{operatingExpense ? "Selecione a categoria" : "Não se aplica"}</option>
						{EXPENSE_CATEGORIES.map((category) => (
							<option key={category.id} value={category.id}>
								{category.name}
							</option>
						))}
					</select>
				</label>
			</div>

			{suggestion && (
				<p className="mt-3 rounded-xl bg-sky-50 px-3 py-2 text-xs leading-5 text-sky-900">
					Sugestão pela categoria cadastrada de {candidate.supplier?.name}:{" "}
					<strong>
						{suggestion.destination}
						{suggestion.expenseCategory ? ` → ${suggestion.expenseCategory.name}` : ""}
					</strong>
					. Você pode corrigir antes de reconhecer.
				</p>
			)}

			<div className="mt-4 flex flex-col gap-3 border-t border-stone-100 pt-4 lg:flex-row lg:items-end">
				<label className="grid flex-1 gap-1.5 text-xs font-medium text-stone-600">
					Motivo para descartar
					<input
						value={dismissReason}
						onChange={(event) => setDismissReason(event.target.value)}
						placeholder="Ex.: cobrança duplicada ou não pertence à SINDI"
						className="h-10 rounded-xl border border-stone-200 px-3 text-sm font-normal text-stone-900 outline-none focus:border-emerald-700"
					/>
				</label>
				<button
					type="button"
					disabled={!dismissReason.trim() || dismissalPending || recognitionPending}
					onClick={() => onDismiss(dismissReason.trim())}
					className="h-10 rounded-xl border border-stone-200 px-4 text-sm font-semibold text-stone-600 transition hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-50"
				>
					{dismissalPending ? "Descartando…" : "Descartar"}
				</button>
				<button
					type="button"
					disabled={!validClassification || recognitionPending || dismissalPending}
					onClick={() =>
						onRecognize({
							candidateId: candidate.id,
							destination: destination.trim(),
							expenseCategoryId: operatingExpense && expenseCategoryId ? expenseCategoryId : null,
						})
					}
					className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-emerald-950 px-4 text-sm font-semibold text-white transition hover:bg-emerald-900 disabled:cursor-not-allowed disabled:opacity-50"
				>
					{recognitionPending && <LoaderCircle className="size-4 animate-spin" />}
					{recognitionPending
						? "Reconhecendo…"
						: candidate.postedPayment
							? "Reconhecer e conciliar o pagamento"
							: "Reconhecer e enviar para A Pagar"}
				</button>
			</div>
			{error && (
				<p className="mt-3 text-sm text-red-700">
					{error.message || "Não foi possível registrar a decisão."}
				</p>
			)}
		</section>
	);
}

function Badge({
	children,
	tone,
}: {
	children: React.ReactNode;
	tone: "green" | "blue" | "amber" | "stone";
}) {
	const className = {
		green: "border-emerald-200 bg-emerald-50 text-emerald-800",
		blue: "border-sky-200 bg-sky-50 text-sky-800",
		amber: "border-amber-200 bg-amber-50 text-amber-800",
		stone: "border-stone-200 bg-stone-50 text-stone-600",
	}[tone];

	return (
		<span
			className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${className}`}
		>
			{children}
		</span>
	);
}

function Fact({ label, value }: { label: string; value: string }) {
	return (
		<div>
			<dt className="text-[10px] font-medium uppercase tracking-wide text-stone-400">{label}</dt>
			<dd className="mt-0.5 font-semibold text-stone-700">{value}</dd>
		</div>
	);
}

function syncDescription(sync: InboxList["sync"]) {
	if (!sync) {
		return "A Caixa ainda não possui uma leitura da Qive.";
	}
	if (sync.state === "failed") {
		return `A última leitura da Qive falhou e a captura anterior foi preservada: ${sync.error ?? "falha sem detalhe"}.`;
	}
	if (sync.state === "running") {
		return "A Qive está sendo consultada agora; os candidatos anteriores permanecem disponíveis.";
	}
	if (sync.rejectedCount > 0) {
		return `Qive atualizada com ${sync.rejectedCount} boleto(s) rejeitado(s); ${sync.candidateCount} candidatos foram observados.`;
	}

	return `Qive atualizada ${sync.finishedAt ? FinancePresentation.moment(sync.finishedAt) : ""} · ${sync.candidateCount} candidatos observados.`;
}
